import { useEffect, useState, useCallback } from 'react';
import { useAdmin } from '@/hooks/useAdmin';
import { Loader2, BarChart3, RefreshCw, AlertCircle, Zap, DollarSign, Activity, Filter } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WINDOWS = [
  { id: '7d', label: '7 dana' },
  { id: '30d', label: '30 dana' },
  { id: '90d', label: '90 dana' },
];

function fmtUsd(n) {
  if (!n) return '$0.00';
  if (n < 0.01) return `$${n.toFixed(6)}`;
  return `$${n.toFixed(4)}`;
}
function fmtN(n) { return new Intl.NumberFormat('hr-HR').format(n || 0); }

export default function AdminLLMUsage() {
  const { authFetch } = useAdmin();
  const [windowSel, setWindowSel] = useState('7d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState({ items: [], total: 0, page: 1 });
  const [logFilter, setLogFilter] = useState({ model: '', route: '', success: '' });
  const [logPage, setLogPage] = useState(1);

  const loadSummary = useCallback(async () => {
    setLoading(true);
    const r = await authFetch(`${API}/admin/llm/usage/summary?window=${windowSel}`);
    const d = await r.json();
    setData(d);
    setLoading(false);
  }, [authFetch, windowSel]);

  const loadLogs = useCallback(async () => {
    const params = new URLSearchParams({ window: windowSel, page: String(logPage), page_size: '25' });
    if (logFilter.model) params.set('model', logFilter.model);
    if (logFilter.route) params.set('route', logFilter.route);
    if (logFilter.success !== '') params.set('success', logFilter.success);
    const r = await authFetch(`${API}/admin/llm/usage/logs?${params.toString()}`);
    setLogs(await r.json());
  }, [authFetch, windowSel, logFilter, logPage]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadLogs(); }, [loadLogs]);

  const totals = data?.totals || {};
  const successRate = totals.calls ? ((totals.successes / totals.calls) * 100).toFixed(1) : '—';

  return (
    <div className="p-6 max-w-[1400px]" data-testid="admin-llm-usage-page">
      <header className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-serif text-3xl text-zinc-100 flex items-center gap-3">
            <BarChart3 size={28} className="text-red-500" /> AI potrošnja Universal Key
          </h1>
          <p className="text-xs text-zinc-500 mt-1">
            Praćenje stvarnih poziva LLM modela kroz cijelu platformu. Procjena tokena ~chars/4; cijene iz statičke tablice (`services/llm_usage.py`).
          </p>
        </div>
        <div className="flex items-center gap-2">
          {WINDOWS.map(w => (
            <button key={w.id} onClick={() => setWindowSel(w.id)}
                    data-testid={`window-${w.id}`}
                    className={`px-3 py-1.5 rounded-md text-xs border transition-colors ${windowSel === w.id ? 'bg-red-600 border-red-600 text-white' : 'bg-zinc-900 border-zinc-800 text-zinc-300 hover:border-zinc-700'}`}>
              {w.label}
            </button>
          ))}
          <button onClick={() => { loadSummary(); loadLogs(); }} data-testid="refresh-btn"
                  className="px-3 py-1.5 rounded-md text-xs border border-zinc-800 bg-zinc-900 hover:border-zinc-700 flex items-center gap-1">
            <RefreshCw size={12} /> Osvježi
          </button>
        </div>
      </header>

      {loading && !data ? (
        <div className="p-10 text-center text-zinc-500"><Loader2 className="inline animate-spin mr-2" /> Učitavanje…</div>
      ) : (
        <>
          {/* ── KPI cards ── */}
          <div className="grid md:grid-cols-4 gap-3 mb-6">
            <KpiCard icon={Activity} label="Ukupno poziva" value={fmtN(totals.calls)} sub={`uspjeh ${successRate}%`} accent="zinc" testid="kpi-calls" />
            <KpiCard icon={Zap} label="Tokena (in/out)" value={`${fmtN(totals.tokens_in)} / ${fmtN(totals.tokens_out)}`} sub={`prosj. latencija ${totals.avg_latency_ms || 0} ms`} accent="amber" testid="kpi-tokens" />
            <KpiCard icon={DollarSign} label="Procij. trošak (sve)" value={fmtUsd(totals.cost_usd)} sub={`besplatnih: ${fmtN(totals.free_calls)}`} accent="emerald" testid="kpi-cost" />
            <KpiCard icon={DollarSign} label="Trošak (samo plaćeno)" value={fmtUsd(totals.cost_paid_usd)} sub="bez besplatnog Gemini puta" accent="red" testid="kpi-paid" />
          </div>

          {/* ── Charts row ── */}
          <div className="grid lg:grid-cols-2 gap-4 mb-6">
            <Panel title="Po modelu" testid="panel-by-model">
              <BarTable rows={data?.by_model || []} keyField={(r) => `${r.provider}/${r.model}`} columns={[
                { label: 'Model', render: (r) => (<span><span className="text-zinc-500 mr-1">{r.provider}/</span>{r.model}</span>) },
                { label: 'Pozivi', render: (r) => fmtN(r.calls), right: true },
                { label: 'Tokena', render: (r) => fmtN(r.tokens_in + r.tokens_out), right: true },
                { label: 'Trošak', render: (r) => fmtUsd(r.cost_usd), right: true },
              ]} maxValue={(data?.by_model || []).reduce((m, r) => Math.max(m, r.calls), 0)} valueOf={(r) => r.calls} />
            </Panel>
            <Panel title="Po ruti / API endpointu" testid="panel-by-route">
              <BarTable rows={data?.by_route || []} keyField={(r) => r.route} columns={[
                { label: 'Ruta', render: (r) => <span className="break-all">{r.route}</span> },
                { label: 'Pozivi', render: (r) => fmtN(r.calls), right: true },
                { label: 'Greške', render: (r) => r.errors > 0 ? <span className="text-red-400">{r.errors}</span> : '—', right: true },
                { label: 'Trošak', render: (r) => fmtUsd(r.cost_usd), right: true },
              ]} maxValue={(data?.by_route || []).reduce((m, r) => Math.max(m, r.calls), 0)} valueOf={(r) => r.calls} />
            </Panel>
          </div>

          <div className="grid lg:grid-cols-2 gap-4 mb-6">
            <Panel title="Po prompt ključu" testid="panel-by-prompt">
              <BarTable rows={data?.by_prompt || []} keyField={(r) => r.prompt_key} columns={[
                { label: 'Prompt key', render: (r) => <span className="font-mono text-xs">{r.prompt_key}</span> },
                { label: 'Pozivi', render: (r) => fmtN(r.calls), right: true },
                { label: 'Trošak', render: (r) => fmtUsd(r.cost_usd), right: true },
              ]} maxValue={(data?.by_prompt || []).reduce((m, r) => Math.max(m, r.calls), 0)} valueOf={(r) => r.calls} />
            </Panel>
            <Panel title="Trend po danima" testid="panel-daily">
              <DailyChart rows={data?.daily || []} />
            </Panel>
          </div>

          {/* ── Call log ── */}
          <Panel title="Call log" testid="panel-logs">
            <div className="flex items-center gap-2 flex-wrap p-3 border-b border-zinc-800 text-xs">
              <Filter size={12} className="text-zinc-500" />
              <select value={logFilter.success} onChange={(e) => { setLogPage(1); setLogFilter(f => ({ ...f, success: e.target.value })); }}
                      data-testid="log-filter-success"
                      className="bg-zinc-900 border border-zinc-800 rounded px-2 py-1">
                <option value="">Status: svi</option>
                <option value="true">Uspjeh</option>
                <option value="false">Greška</option>
              </select>
              <input placeholder="Model (gemini-2.5-flash)" value={logFilter.model}
                     onChange={(e) => { setLogPage(1); setLogFilter(f => ({ ...f, model: e.target.value })); }}
                     data-testid="log-filter-model"
                     className="bg-zinc-900 border border-zinc-800 rounded px-2 py-1 w-56" />
              <input placeholder="Ruta" value={logFilter.route}
                     onChange={(e) => { setLogPage(1); setLogFilter(f => ({ ...f, route: e.target.value })); }}
                     data-testid="log-filter-route"
                     className="bg-zinc-900 border border-zinc-800 rounded px-2 py-1 w-72" />
              <span className="ml-auto text-zinc-500">{fmtN(logs.total)} poziva</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-zinc-900/60 text-zinc-400">
                  <tr>
                    <th className="text-left px-3 py-2">Vrijeme</th>
                    <th className="text-left px-3 py-2">Prompt</th>
                    <th className="text-left px-3 py-2">Model</th>
                    <th className="text-left px-3 py-2">Ruta</th>
                    <th className="text-right px-3 py-2">Tok. in</th>
                    <th className="text-right px-3 py-2">Tok. out</th>
                    <th className="text-right px-3 py-2">Latencija</th>
                    <th className="text-right px-3 py-2">Trošak</th>
                    <th className="text-center px-3 py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.items.map((it) => (
                    <tr key={it.id} className="border-t border-zinc-800/50 hover:bg-zinc-900/40" data-testid={`log-row-${it.id}`}>
                      <td className="px-3 py-2 text-zinc-500 whitespace-nowrap">{new Date(it.created_at).toLocaleString('hr-HR')}</td>
                      <td className="px-3 py-2 font-mono text-zinc-300">{it.prompt_key}</td>
                      <td className="px-3 py-2 text-zinc-400">{it.model}</td>
                      <td className="px-3 py-2 text-zinc-500 truncate max-w-[260px]" title={it.route}>{it.route}</td>
                      <td className="px-3 py-2 text-right text-zinc-400">{fmtN(it.tokens_in)}</td>
                      <td className="px-3 py-2 text-right text-zinc-400">{fmtN(it.tokens_out)}</td>
                      <td className="px-3 py-2 text-right text-zinc-500">{it.latency_ms} ms</td>
                      <td className="px-3 py-2 text-right text-zinc-300">{it.is_free ? <span className="text-emerald-500">free</span> : fmtUsd(it.cost_usd)}</td>
                      <td className="px-3 py-2 text-center">
                        {it.success
                          ? <span className="px-1.5 py-0.5 rounded bg-emerald-900/40 text-emerald-400">OK</span>
                          : <span className="px-1.5 py-0.5 rounded bg-red-900/40 text-red-400 inline-flex items-center gap-1" title={it.error}><AlertCircle size={10} /> ERR</span>}
                      </td>
                    </tr>
                  ))}
                  {logs.items.length === 0 && (
                    <tr><td colSpan={9} className="text-center text-zinc-600 py-10">Nema poziva u ovom rasponu.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between p-3 text-xs text-zinc-500 border-t border-zinc-800">
              <button disabled={logPage <= 1} onClick={() => setLogPage(p => p - 1)}
                      data-testid="log-page-prev"
                      className="px-2 py-1 rounded bg-zinc-900 border border-zinc-800 disabled:opacity-30">← Prethodna</button>
              <span>Stranica {logs.page}</span>
              <button disabled={logs.items.length < 25} onClick={() => setLogPage(p => p + 1)}
                      data-testid="log-page-next"
                      className="px-2 py-1 rounded bg-zinc-900 border border-zinc-800 disabled:opacity-30">Sljedeća →</button>
            </div>
          </Panel>
        </>
      )}
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, sub, accent, testid }) {
  const accents = {
    zinc: 'text-zinc-300', amber: 'text-amber-400', emerald: 'text-emerald-400', red: 'text-red-400',
  };
  return (
    <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4" data-testid={testid}>
      <div className="text-[10px] text-zinc-500 uppercase tracking-wider flex items-center gap-1.5">
        <Icon size={11} /> {label}
      </div>
      <div className={`mt-1 text-xl font-semibold ${accents[accent] || 'text-zinc-200'}`}>{value}</div>
      <div className="text-[10px] text-zinc-500 mt-0.5">{sub}</div>
    </div>
  );
}

function Panel({ title, children, testid }) {
  return (
    <section className="bg-zinc-900/40 border border-zinc-800 rounded-xl overflow-hidden" data-testid={testid}>
      <header className="px-4 py-2 border-b border-zinc-800 text-xs uppercase tracking-wider text-zinc-400">{title}</header>
      <div>{children}</div>
    </section>
  );
}

function BarTable({ rows, columns, keyField, maxValue, valueOf }) {
  if (!rows.length) return <div className="p-6 text-xs text-zinc-600 text-center">Nema podataka.</div>;
  return (
    <div>
      <table className="w-full text-xs">
        <thead className="bg-zinc-900/60 text-zinc-500">
          <tr>{columns.map(c => <th key={c.label} className={`px-3 py-2 ${c.right ? 'text-right' : 'text-left'}`}>{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map(r => {
            const pct = maxValue ? (valueOf(r) / maxValue) * 100 : 0;
            return (
              <tr key={keyField(r)} className="border-t border-zinc-800/50 hover:bg-zinc-900/40 relative">
                <td className="absolute inset-0 pointer-events-none" colSpan={columns.length}>
                  <div className="h-full bg-red-600/10" style={{ width: `${pct}%` }} />
                </td>
                {columns.map(c => (
                  <td key={c.label} className={`relative px-3 py-2 ${c.right ? 'text-right text-zinc-300' : 'text-zinc-300'}`}>{c.render(r)}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function DailyChart({ rows }) {
  if (!rows.length) return <div className="p-6 text-xs text-zinc-600 text-center">Nema podataka.</div>;
  const maxCalls = rows.reduce((m, r) => Math.max(m, r.calls), 0) || 1;
  return (
    <div className="p-4 space-y-1">
      {rows.map(r => (
        <div key={r.date} className="flex items-center gap-2 text-xs">
          <span className="w-24 text-zinc-500 font-mono">{r.date}</span>
          <div className="flex-1 h-5 bg-zinc-900/60 rounded-sm overflow-hidden relative">
            <div className="h-full bg-gradient-to-r from-red-600 to-amber-500" style={{ width: `${(r.calls / maxCalls) * 100}%` }} />
            <span className="absolute inset-0 px-2 flex items-center text-[10px] text-zinc-200">{r.calls} poz.</span>
          </div>
          <span className="w-20 text-right text-zinc-400">{fmtUsd(r.cost_usd)}</span>
        </div>
      ))}
    </div>
  );
}
